import {
  Component,
  inject,
  signal,
  computed,
  PLATFORM_ID,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { CookieConsentService } from '@core/services/cookie-consent/cookie-consent.service';
import { UserConsentService } from '@core/services/user-consent/user-consent.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { TranslocoDirective } from '@jsverse/transloco';

/**
 * Cookie Consent Banner Component
 * Displays GDPR/LOPD compliant cookie consent banner and settings modal
 */
@Component({
  selector: 'd2d-cookie-consent-banner',
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    ToggleSwitchModule,
    FormsModule,
    TranslocoDirective,
  ],
  templateUrl: './cookie-consent-banner.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './cookie-consent-banner.component.scss',
})
export class CookieConsentBannerComponent {
  private cookieService = inject(CookieConsentService);
  private userConsentService = inject(UserConsentService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private readonly navigationService = inject(NavigationService);

  /** Whether we're running in the browser */
  private isBrowser = isPlatformBrowser(this.platformId);

  /** Current route URL as a signal */
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    ),
    { initialValue: { url: this.router.url } as NavigationEnd },
  );

  /** Whether we're on the cookie policy page */
  private isOnCookiePolicyPage = computed(() => {
    const url = this.currentUrl()?.url || '';
    return url.includes('/politica-cookies');
  });

  /** Whether the banner should be visible */
  showBanner = computed(() => {
    // Don't show banner during SSR
    if (!this.isBrowser) {
      return false;
    }

    const onPolicyPage = this.isOnCookiePolicyPage();
    const serviceShouldShow = this.cookieService.shouldShowBanner();

    // Don't show banner on cookie policy page
    if (onPolicyPage) {
      return false;
    }

    // Otherwise, use the service's logic
    return serviceShouldShow;
  });

  /** Whether the settings modal is visible */
  showSettings = signal(false);

  /** Temporary preferences for the settings modal */
  preferences = signal({
    analytics: false,
    marketing: false,
    preferences: false,
  });

  /**
   * Accept all cookies
   */
  acceptAll(): void {
    this.cookieService.acceptAll();
    this.userConsentService.createConsent('cookie').subscribe();
  }

  /**
   * Reject all non-essential cookies
   */
  rejectAll(): void {
    this.cookieService.rejectAll();
    this.userConsentService.createConsent('cookie').subscribe();
  }

  /**
   * Open settings modal
   */
  openSettings(): void {
    // Load current preferences
    const consent = this.cookieService.getConsent();
    if (consent) {
      this.preferences.set({
        analytics: consent.analytics,
        marketing: consent.marketing,
        preferences: consent.preferences,
      });
    }
    this.showSettings.set(true);
  }

  /**
   * Save custom preferences from settings modal
   */
  savePreferences(): void {
    this.cookieService.savePreferences(this.preferences());
    this.showSettings.set(false);
  }

  /**
   * Close settings modal without saving
   */
  closeSettings(): void {
    this.showSettings.set(false);
  }

  /**
   * Redireccion a la ruta incluyendo el slug si así lo requiere
   */
  goPolicies() {
    this.navigationService.navigate(['politica-cookies']);
  }
}
